import { isSupabaseConfigured, supabase } from "../lib/supabase";

const DELIVERY_ZONES_KEY = "almoune_delivery_zones";
const DELIVERY_SLOTS_KEY = "almoune_delivery_slots";
const CUSTOMER_ADDRESSES_KEY = "almoune_customer_addresses";

const defaultZones = [
  {
    id: "zone-paris",
    name: "Paris et proche banlieue",
    postcode_start: "75000",
    postcode_end: "95999",
    fee: 4.9,
    minimum_order: 0,
    minimum_fresh_order: 25,
    minimum_frozen_order: 59,
    free_delivery_threshold: 49,
    supports_fresh: true,
    supports_frozen: false,
    is_active: true,
  },
  {
    id: "zone-france",
    name: "France metropolitaine",
    postcode_start: "01000",
    postcode_end: "99999",
    fee: 7.9,
    minimum_order: 25,
    minimum_fresh_order: 0,
    minimum_frozen_order: 0,
    free_delivery_threshold: 79,
    supports_fresh: false,
    supports_frozen: false,
    is_active: true,
  },
];

const defaultSlots = [
  {
    id: "slot-standard-morning",
    zone_id: "zone-paris",
    label: "Demain entre 10h et 13h",
    starts_at: "2026-04-05T10:00:00",
    ends_at: "2026-04-05T13:00:00",
    capacity: 20,
    reserved_count: 0,
    is_active: true,
  },
  {
    id: "slot-standard-evening",
    zone_id: "zone-paris",
    label: "Demain entre 18h et 21h",
    starts_at: "2026-04-05T18:00:00",
    ends_at: "2026-04-05T21:00:00",
    capacity: 12,
    reserved_count: 0,
    is_active: true,
  },
];

const normalizeZone = (zone) => ({
  id: zone.id || `zone-${Date.now()}`,
  name: zone.name || "",
  postcode_start: zone.postcode_start || "",
  postcode_end: zone.postcode_end || "",
  fee: Number(zone.fee || 0),
  minimum_order: Number(zone.minimum_order || 0),
  minimum_fresh_order: Number(zone.minimum_fresh_order || 0),
  minimum_frozen_order: Number(zone.minimum_frozen_order || 0),
  free_delivery_threshold:
    zone.free_delivery_threshold === "" || zone.free_delivery_threshold === null
      ? null
      : Number(zone.free_delivery_threshold || 0),
  supports_fresh: zone.supports_fresh ?? true,
  supports_frozen: zone.supports_frozen ?? false,
  is_active: zone.is_active ?? true,
});

const normalizeAddress = (address) => ({
  id: address.id || `addr-${Date.now()}`,
  label: address.label || "Adresse",
  address_type: address.address_type || "shipping",
  full_name: address.full_name || "",
  phone: address.phone || "",
  line1: address.line1 || "",
  line2: address.line2 || "",
  postcode: address.postcode || "",
  city: address.city || "",
  country: address.country || "France",
  is_default: address.is_default ?? false,
});

const normalizeSlot = (slot) => ({
  id: slot.id || `slot-${Date.now()}`,
  zone_id: slot.zone_id || "",
  label: slot.label || "",
  starts_at: slot.starts_at || "",
  ends_at: slot.ends_at || "",
  capacity: Number(slot.capacity || 0),
  reserved_count: Number(slot.reserved_count || 0),
  is_active: slot.is_active ?? true,
});

const readCache = (key, fallback = []) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      window.localStorage.setItem(key, JSON.stringify(fallback));
      return fallback;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeCache = (key, value) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
  return value;
};

export const loadDeliveryZones = async () => {
  const cached = readCache(DELIVERY_ZONES_KEY, defaultZones).map(normalizeZone);

  if (!isSupabaseConfigured || !supabase) {
    return cached;
  }

  const { data, error } = await supabase
    .from("delivery_zones")
    .select(
      "id, name, postcode_start, postcode_end, fee, minimum_order, minimum_fresh_order, minimum_frozen_order, free_delivery_threshold, supports_fresh, supports_frozen, is_active"
    )
    .order("name", { ascending: true });

  if (error || !data || !data.length) {
    return cached;
  }

  return writeCache(DELIVERY_ZONES_KEY, data.map(normalizeZone));
};

export const saveDeliveryZone = async (zone) => {
  const normalized = normalizeZone(zone);

  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_ZONES_KEY, defaultZones).map(normalizeZone);
    const next = cached.some((item) => item.id === normalized.id)
      ? cached.map((item) => (item.id === normalized.id ? normalized : item))
      : [...cached, normalized];
    writeCache(DELIVERY_ZONES_KEY, next);
    return normalized;
  }

  const { data, error } = await supabase
    .from("delivery_zones")
    .upsert([normalized], { onConflict: "id" })
    .select(
      "id, name, postcode_start, postcode_end, fee, minimum_order, minimum_fresh_order, minimum_frozen_order, free_delivery_threshold, supports_fresh, supports_frozen, is_active"
    )
    .single();

  if (error) {
    throw error;
  }

  return normalizeZone(data);
};

export const deleteDeliveryZone = async (id) => {
  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_ZONES_KEY, defaultZones).map(normalizeZone);
    writeCache(
      DELIVERY_ZONES_KEY,
      cached.filter((item) => item.id !== id)
    );
    return true;
  }

  const { error } = await supabase.from("delivery_zones").delete().eq("id", id);
  if (error) {
    throw error;
  }
  return true;
};

export const loadDeliverySlots = async () => {
  const cached = readCache(DELIVERY_SLOTS_KEY, defaultSlots).map(normalizeSlot);

  if (!isSupabaseConfigured || !supabase) {
    return cached;
  }

  const { data, error } = await supabase
    .from("delivery_slots")
    .select(
      "id, zone_id, label, starts_at, ends_at, capacity, reserved_count, is_active"
    )
    .order("starts_at", { ascending: true });

  if (error || !data) {
    return cached;
  }

  return writeCache(DELIVERY_SLOTS_KEY, data.map(normalizeSlot));
};

export const saveDeliverySlot = async (slot) => {
  const normalized = normalizeSlot(slot);

  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_SLOTS_KEY, defaultSlots).map(normalizeSlot);
    const next = cached.some((item) => item.id === normalized.id)
      ? cached.map((item) => (item.id === normalized.id ? normalized : item))
      : [...cached, normalized];
    writeCache(DELIVERY_SLOTS_KEY, next);
    return normalized;
  }

  const { data, error } = await supabase
    .from("delivery_slots")
    .upsert([normalized], { onConflict: "id" })
    .select(
      "id, zone_id, label, starts_at, ends_at, capacity, reserved_count, is_active"
    )
    .single();

  if (error) {
    throw error;
  }

  return normalizeSlot(data);
};

export const deleteDeliverySlot = async (id) => {
  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_SLOTS_KEY, defaultSlots).map(normalizeSlot);
    writeCache(
      DELIVERY_SLOTS_KEY,
      cached.filter((item) => item.id !== id)
    );
    return true;
  }

  const { error } = await supabase.from("delivery_slots").delete().eq("id", id);
  if (error) {
    throw error;
  }
  return true;
};

export const incrementSlotReservation = async (slotId) => {
  if (!slotId) return;
  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_SLOTS_KEY, defaultSlots).map(normalizeSlot);
    const slot = cached.find((s) => s.id === slotId);
    if (slot) {
      slot.reserved_count = Number(slot.reserved_count || 0) + 1;
      writeCache(DELIVERY_SLOTS_KEY, cached);
    }
    return;
  }
  const { data: slot } = await supabase
    .from("delivery_slots")
    .select("reserved_count")
    .eq("id", slotId)
    .maybeSingle();
  if (slot) {
    await supabase
      .from("delivery_slots")
      .update({ reserved_count: Number(slot.reserved_count || 0) + 1 })
      .eq("id", slotId);
  }
};

export const decrementSlotReservation = async (slotId) => {
  if (!slotId) return;
  if (!isSupabaseConfigured || !supabase) {
    const cached = readCache(DELIVERY_SLOTS_KEY, defaultSlots).map(normalizeSlot);
    const slot = cached.find((s) => s.id === slotId);
    if (slot) {
      slot.reserved_count = Math.max(0, Number(slot.reserved_count || 0) - 1);
      writeCache(DELIVERY_SLOTS_KEY, cached);
    }
    return;
  }
  const { data: slot } = await supabase
    .from("delivery_slots")
    .select("reserved_count")
    .eq("id", slotId)
    .maybeSingle();
  if (slot) {
    await supabase
      .from("delivery_slots")
      .update({ reserved_count: Math.max(0, Number(slot.reserved_count || 0) - 1) })
      .eq("id", slotId);
  }
};

const postcodeToNumber = (postcode = "") => {
  const digits = String(postcode).replace(/\D/g, "").slice(0, 5);
  return digits ? Number(digits) : NaN;
};

export const findDeliveryZone = (postcode, zones, requirements = {}) => {
  const code = postcodeToNumber(postcode);
  if (!Number.isFinite(code)) {
    return null;
  }

  return zones.find((zone) => {
    if (!zone.is_active) {
      return false;
    }
    const start = postcodeToNumber(zone.postcode_start);
    const end = postcodeToNumber(zone.postcode_end);
    const inRange =
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      code >= start &&
      code <= end;

    if (!inRange) {
      return false;
    }

    if (requirements.needsFresh && !zone.supports_fresh) {
      return false;
    }

    if (requirements.needsFrozen && !zone.supports_frozen) {
      return false;
    }

    return true;
  });
};

const DIFFICULT_ZONES = [
  "01410", "03120", "03250",
  "04110", "04120", "04130", "04140", "04150", "04160", "04170", "04180", "04190", "04200", "04210", "04240", "04250", "04260", "04310", "04320", "04330", "04340", "04360", "04370", "04400", "04510", "04530", "04850",
  "05100", "05110", "05120", "05130", "05140", "05150", "05160", "05170", "05200", "05220", "05240", "05250", "05260", "05290", "05310", "05320", "05330", "05340", "05350", "05400", "05460", "05500", "05600", "05700", "05800",
  "06140", "06380", "06390", "06410", "06420", "06430", "06440", "06450", "06460", "06470", "06530", "06540", "06620", "06660", "06690", "06710", "06730", "06750", "06790", "06830", "06910",
  "07140", "07150", "07310", "07320", "07330", "07450", "07470", "07510", "07530", "07590", "07660",
  "11140", "11190", "11210", "11330", "11340", "11390", "11420", "11500",
  "12110", "12190", "12220", "12230", "12320", "12360",
  "15110", "15190", "15200", "15300", "15400",
  "17111", "17123", "17190", "17310", "17370", "17410", "17480", "17550", "17580", "17590", "17630", "17650", "17670", "17740", "17840", "17880", "17940",
  "22870",
  "20000", "20110", "20120", "20130", "20140", "20150", "20160", "20200", "20210", "20220", "20230", "20240", "20250", "20260", "20270", "20290", "20600", "20620",
  "29242", "29253", "29259", "29990",
  "30570",
  "31110", "31380", "31440",
  "34220", "34390",
  "38112", "38114", "38142", "38190", "38250", "38350", "38380", "38410", "38580", "38650", "38660", "38680", "38740", "38750", "38770", "38860", "38880", "38930", "38970",
  "39130", "39150", "39200", "39220", "39310", "39400", "39430", "39680", "39800",
  "42660", "42920",
  "43150",
  "48000",
  "50140",
  "56360", "56590", "56780", "56840",
  "63113", "63114", "63210", "63240", "63610", "63660", "63680", "63690", "63840",
  "64130", "64150", "64280", "64440", "64470", "64490", "64560", "64570",
  "65110", "65120", "65150", "65170", "65200", "65240", "65400", "65510", "65590", "65710", "65740",
  "66120", "66190", "66210", "66260", "66320", "66360", "66730", "66760", "66800",
  "70440",
  "73110", "73120", "73130", "73140", "73150", "73160", "73170", "73200", "73210", "73220", "73230", "73250", "73260", "73270", "73300", "73320", "73340", "73350", "73390", "73440", "73450", "73460", "73470", "73500", "73530", "73550", "73590", "73600", "73620", "73630", "73640", "73710", "73730", "73790",
  "74110", "74120", "74170", "74220", "74230", "74260", "74310", "74340", "74350", "74360", "74390", "74400", "74420", "74430", "74440", "74450", "74470", "74480", "74660", "74740", "74920",
  "81140", "81260", "81320", "81530",
  "83111", "83530", "83630", "83690", "83840",
  "84390",
  "85330", "85350", "85680",
  "88210", "88240", "88310", "88340", "88370", "88400"
];

export const calculateDeliveryForCart = ({
  postcode,
  subtotal,
  items,
  zones,
}) => {
  const needsFresh = items.some((item) => item.product?.type === "fresh");
  const needsFrozen = items.some((item) => item.product?.type === "frozen");
  
  const freshSubtotal = items
    .filter((item) => item.product?.type === "fresh")
    .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
    
  const frozenSubtotal = items
    .filter((item) => item.product?.type === "frozen")
    .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
    
  const coldSubtotal = freshSubtotal + frozenSubtotal;
  const regularSubtotal = items
    .filter((item) => item.product?.type !== "fresh" && item.product?.type !== "frozen")
    .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);

  const zone = findDeliveryZone(postcode, zones, { needsFresh, needsFrozen });

  if (!zone) {
    return {
      zone: null,
      fee: null,
      regularFee: null,
      coldFee: null,
      eligible: false,
      regularSubtotal,
      coldSubtotal,
      freshSubtotal,
      frozenSubtotal,
      message: "Aucune zone de livraison compatible avec ce code postal.",
    };
  }

  if (subtotal < Number(zone.minimum_order || 0)) {
    return {
      zone,
      fee: Number(zone.fee || 0),
      regularFee: Number(zone.fee || 0),
      coldFee: 0,
      eligible: false,
      regularSubtotal,
      coldSubtotal,
      freshSubtotal,
      frozenSubtotal,
      message: `Commande minimum ${Number(zone.minimum_order || 0).toFixed(2)} EUR pour cette zone.`,
    };
  }

  const threshold = Number(zone.free_delivery_threshold || 0);
  
  // Calculate regular fee
  let regularFee = 0;
  if (regularSubtotal > 0) {
    regularFee = (threshold > 0 && regularSubtotal >= threshold) ? 0 : Number(zone.fee || 0);
  }

  // Calculate cold fee (Fresh/Frozen)
  let coldFee = 0;
  if (coldSubtotal > 0) {
    if (coldSubtotal < 49) {
      return {
        zone,
        fee: null,
        regularFee: null,
        coldFee: null,
        eligible: false,
        regularSubtotal,
        coldSubtotal,
        freshSubtotal,
        frozenSubtotal,
        message: "En dessous de 49 EUR, la livraison de produits frais ou surgelés n'est pas possible.",
      };
    } else if (coldSubtotal >= 79) {
      coldFee = 3.90;
    } else {
      coldFee = 6.90;
    }
    
    // Add 3.00 EUR for difficult zones
    if (postcode && DIFFICULT_ZONES.includes(String(postcode).trim())) {
      coldFee += 3.00;
    }
  }

  const totalFee = regularFee + coldFee;

  return {
    zone,
    fee: totalFee,
    regularFee,
    coldFee,
    eligible: true,
    regularSubtotal,
    coldSubtotal,
    freshSubtotal,
    frozenSubtotal,
    message: totalFee === 0 
      ? `Livraison offerte dans la zone ${zone.name}.`
      : `Livraison ${zone.name} : ${totalFee.toFixed(2)} EUR.`,
  };
};

export const getAvailableSlotsForZone = (zoneId = "", slots = []) =>
  slots.filter(
    (slot) =>
      slot.zone_id === zoneId &&
      slot.is_active &&
      Number(slot.capacity || 0) > Number(slot.reserved_count || 0)
  );

export const validateProductZoneRestrictions = (items = [], zoneId = "") => {
  const blockedItems = items.filter((item) => {
    const allowedZones = item.product?.allowedZoneIds || [];
    return allowedZones.length > 0 && (!zoneId || !allowedZones.includes(zoneId));
  });

  if (!blockedItems.length) {
    return {
      allowed: true,
      blockedItems: [],
      message: "",
    };
  }

  return {
    allowed: false,
    blockedItems,
    message: `Produits non disponibles dans cette zone: ${blockedItems
      .map((item) => item.title || item.product?.name || "Produit")
      .join(", ")}.`,
  };
};

export const loadCustomerAddresses = async (profileId = "") => {
  const cached = readCache(CUSTOMER_ADDRESSES_KEY, []).map(normalizeAddress);

  if (!profileId || !isSupabaseConfigured || !supabase) {
    return cached;
  }

  const { data, error } = await supabase
    .from("addresses")
    .select(
      "id, label, address_type, full_name, phone, line1, line2, postcode, city, country, is_default"
    )
    .eq("profile_id", profileId)
    .order("address_type", { ascending: true })
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: false });

  if (error || !data) {
    return cached;
  }

  return writeCache(CUSTOMER_ADDRESSES_KEY, data.map(normalizeAddress));
};

export const saveCustomerAddress = async (profileId = "", address) => {
  const normalized = normalizeAddress(address);

  if (!profileId || !isSupabaseConfigured || !supabase) {
    const cached = readCache(CUSTOMER_ADDRESSES_KEY, []).map(normalizeAddress);
    const next = cached.some((item) => item.id === normalized.id)
      ? cached.map((item) => (item.id === normalized.id ? normalized : item))
      : [normalized, ...cached];
    writeCache(CUSTOMER_ADDRESSES_KEY, next);
    return normalized;
  }

  if (normalized.is_default) {
    await supabase
      .from("addresses")
      .update({ is_default: false })
      .eq("profile_id", profileId)
      .eq("address_type", normalized.address_type);
  }

  const payload = {
    ...normalized,
    profile_id: profileId,
  };

  const { data, error } = await supabase
    .from("addresses")
    .upsert([payload], { onConflict: "id" })
    .select(
      "id, label, address_type, full_name, phone, line1, line2, postcode, city, country, is_default"
    )
    .single();

  if (error) {
    throw error;
  }

  return normalizeAddress(data);
};

export const getDefaultAddressByType = (addresses = [], addressType = "shipping") =>
  addresses.find(
    (address) => address.address_type === addressType && address.is_default
  ) ||
  addresses.find((address) => address.address_type === addressType) ||
  null;

export const getAddressesByType = (addresses = [], addressType = "shipping") =>
  addresses.filter((address) => address.address_type === addressType);
